/**
 * Sharing — the contract.
 *
 * Ported from TMS `src/components/SharePage.tsx`, which already models a
 * Google-Drive-style share dialog. Two things change here:
 *
 * 1. TMS shares one thing: a PAGE, with users of the only company that exists.
 *    Numtable shares two: a MODULE with a member of the organization
 *    (`module_permissions`), or a RESOURCE — one spreadsheet, one booking page —
 *    which may go to someone OUTSIDE the organization (`resource_shares`).
 *    Same dialog, two subjects.
 *
 * 2. TMS talks to Supabase from inside the component. This layer does not: the
 *    UI is given a `ShareAdapter`. Granting access to an email that is not a
 *    user yet has to create an invitation, and invitations are Worker-only
 *    (CLAUDE.md invariant 4) — a component cannot be allowed to decide that.
 */

/** Effective access for a sheet/module/resource. Ordered from least to most capable. */
export type AccessLevel = 'view' | 'edit_data' | 'edit_format' | 'edit_formula';

export type AccessOrigin = 'role' | 'module' | 'folders' | 'file';

/** Converts the pre-migration `edit` value while old rows are still readable. */
export function normalizeAccessLevel(value: string): AccessLevel {
  if (value === 'edit') return 'edit_data';
  if (value === 'edit_format' || value === 'edit_formula' || value === 'edit_data') return value;
  return 'view';
}

/**
 * Two axes in one union, matching the schema.
 *
 * `owner` > `admin` > `coordinator` > `member` > `viewer` is the people ladder.
 * `developer` is off it: technical capabilities, no authority over anyone.
 */
export type MemberRole =
  | 'owner'
  | 'admin'
  | 'coordinator'
  | 'member'
  | 'viewer'
  | 'developer'
  | 'designer';

/**
 * What is being shared.
 *
 * `module` grants live in `module_permissions` and only apply to people who are
 * already members. `resource` grants live in `resource_shares` and deliberately
 * do NOT make the recipient a member — see docs/ARCHITECTURE.md §0.
 */
export type ShareSubject =
  | { kind: 'module'; orgId: string; moduleKey: string; name: string }
  | {
      kind: 'resource';
      orgId: string;
      resourceType: string;
      resourceId: string;
      name: string;
    };

export interface SharePrincipal {
  userId: string;
  /** May be null: a person invited by email has no profile until they accept. */
  name: string | null;
  email: string;
  /** Organization role, or null for an outside collaborator. */
  role: MemberRole | null;
  /** Optional effective level for inherited principals. */
  level?: AccessLevel;
  /** Technical managers may be able to limit inherited structural access. */
  canManage?: boolean;
}

export interface ShareGrant extends SharePrincipal {
  level: AccessLevel;
  /** Where the effective level came from, when supplied by the adapter. */
  origin?: AccessOrigin;
  /**
   * Whether the current user may change or revoke THIS grant. Per person, not
   * per dialog: a coordinator manages the members of their own team and nobody
   * else, so the same list mixes rows they can edit with rows they cannot.
   * Presentation only — RLS is the enforcement.
   */
  canManage: boolean;
}

/** Someone who opened the link without access and asked for it. */
export interface AccessRequest {
  requestId: string;
  email: string;
  createdAt: string;
}

export interface PendingInvite {
  invitationId: string;
  email: string;
  level: AccessLevel;
  expiresAt: string;
  /**
   * Present when no email was sent (RESEND_API_KEY not configured). The owner
   * must copy and forward this link themselves.
   */
  inviteLink?: string;
}

export interface ShareAccess {
  /** Explicit grants. Editable and revocable. */
  grants: ShareGrant[];
  /**
   * Access that comes from the org role — owners and admins bypass
   * `module_permissions`. Shown read-only, exactly as Folders shows the owner.
   */
  inherited: SharePrincipal[];
  /**
   * Members who could be granted access. MUST be scoped to `subject.orgId`:
   * this list is a user directory, and a directory that crosses tenants is a
   * data leak, not a convenience.
   */
  candidates: SharePrincipal[];
  pendingInvites: PendingInvite[];
  /** Waiting for the current user to approve or dismiss; absent when unsupported. */
  accessRequests?: AccessRequest[];
  /**
   * Whether the current user may add anyone at all. Per-person rights live on
   * each `ShareGrant`. Presentation only; RLS is the enforcement.
   */
  canAdd: boolean;
  /** General-link access, when this subject supports a public URL. */
  publicAccess?: PublicAccess;
}

export interface PublicAccess {
  readonly url: string;
  readonly isPublic: boolean;
  readonly canManage: boolean;
}

export type ShareErrorCode =
  | 'notAllowed'
  | 'alreadyHasAccess'
  | 'notFound'
  | 'invalidEmail'
  /**
   * The address is well-formed but nobody in this organization uses it.
   *
   * Distinct from `invalidEmail` because the fix is different — invite them, or
   * check the spelling — and distinct from `notFound`, which is about the thing
   * being shared. The lookup is deliberately scoped to the organization, so
   * this never doubles as a way to ask whether an account exists at all.
   */
  | 'noSuchUser'
  | 'unknown';

/** Lets the adapter report a cause the dialog can translate. */
export class ShareError extends Error {
  readonly code: ShareErrorCode;

  constructor(code: ShareErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'ShareError';
    this.code = code;
  }
}

/** Result of adding someone by email. */
export type AddOutcome =
  | { type: 'granted' }
  /**
   * The email had no account: an invitation was created.
   * `inviteLink` is present when no email was sent (RESEND not configured)
   * and the sharer must copy the link and forward it themselves.
   */
  | { type: 'invited'; inviteLink?: string };

/**
 * Everything the dialog needs. Implemented per subject kind in the consuming
 * module's `queries.ts`, never inside these components.
 */
export interface ShareAdapter {
  load(subject: ShareSubject, signal: AbortSignal): Promise<ShareAccess>;
  /**
   * Add by email. The adapter decides between a direct grant and an
   * invitation — the component must not, because inviting is a Worker call.
   */
  add(subject: ShareSubject, email: string, level: AccessLevel): Promise<AddOutcome>;
  changeLevel(subject: ShareSubject, userId: string, level: AccessLevel): Promise<void>;
  revoke(subject: ShareSubject, userId: string): Promise<void>;
  cancelInvite(subject: ShareSubject, invitationId: string): Promise<void>;
  setPublic?(subject: ShareSubject, isPublic: boolean): Promise<void>;
  /** Grants view access to the requester, then clears the request. */
  approveRequest?(subject: ShareSubject, request: AccessRequest): Promise<void>;
  dismissRequest?(subject: ShareSubject, requestId: string): Promise<void>;
}
